import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, ShieldX, Download, ExternalLink, QrCode, Search, Award, ScrollText, Users } from "lucide-react";
import { EnvironmentBanner, EnvironmentChip } from "@/components/EnvironmentBanner";

/**
 * Public Certificate Verification Portal — /verify/certificate/{num?}
 *
 * Zero-auth court/bank/regulator/consumer verification page.
 * Renders the live cert state, counter-signatures, badge, PDF + JSON links.
 */
export default function VerifyCertificatePortal() {
  const { certNumber } = useParams();
  const [query, setQuery] = useState(certNumber || "");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const base = process.env.REACT_APP_BACKEND_URL || "";

  const doLookup = async (num) => {
    const q = (num ?? query).trim();
    if (!q) return toast.error("Enter a certificate number");
    setBusy(true); setNotFound(false); setData(null);
    try {
      const r = await fetch(`${base}/api/public/certificates/${encodeURIComponent(q)}/json`);
      if (r.status === 404) { setNotFound(true); return; }
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
    } catch (e) { toast.error(`Lookup failed: ${e.message}`); }
    finally { setBusy(false); }
  };

  // Auto-lookup on mount if we have a URL param
  useState(() => { if (certNumber) doLookup(certNumber); });

  const cert = data?.certificate;
  const state = cert?.status || cert?.effective_status || cert?.outcome;
  const tone = state === "valid" ? "text-emerald-700 border-emerald-500/40 bg-emerald-500/5"
             : state === "expired" ? "text-amber-700 border-amber-500/40 bg-amber-500/5"
             : ["suspended", "revoked", "not_verified", "certificate_revoked"].includes(state)
               ? "text-red-700 border-red-500/40 bg-red-500/5"
               : "text-muted-foreground border-border bg-card";
  const Icon = state === "valid" ? ShieldCheck
             : ["revoked", "certificate_revoked", "not_verified"].includes(state) ? ShieldX
             : ShieldAlert;

  return (
    <div>
      <Navbar />
      <div className="max-w-4xl mx-auto px-6 py-10">
        <div className="mb-6">
          <div className="kicker">Public · Trust Verification Portal</div>
          <h1 className="display text-4xl">Verify a THOM Verify™ Certificate</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Enter a certificate number (e.g. <code>TVC-AFR-…</code>) to verify its authenticity, current lifecycle status, counter-signatures and legal template. This portal is public and free — no login required. Courts, banks, insurers, customs authorities and regulators may rely on this page for real-time verification.
          </p>
        </div>

        <div className="flex gap-2 mb-6" data-testid="verify-search">
          <Input value={query} onChange={(e) => setQuery(e.target.value.toUpperCase())} placeholder="TVC-AFR-…" data-testid="verify-input" onKeyDown={(e) => e.key === "Enter" && doLookup()} />
          <Button onClick={() => doLookup()} disabled={busy} data-testid="verify-lookup"><Search className="h-4 w-4 mr-1" /> Verify</Button>
        </div>

        {notFound && (
          <div className="border border-red-500/40 bg-red-500/5 p-6 text-center" data-testid="verify-not-found">
            <ShieldX className="h-8 w-8 mx-auto text-red-600 mb-2" />
            <div className="font-semibold">Certificate not found</div>
            <div className="text-sm text-muted-foreground mt-1">This number is not registered in the THOM Verify™ trust ledger. Verify the digits and try again.</div>
          </div>
        )}

        {cert && (
          <div className="space-y-5" data-testid="verify-result">
            {/* Environment banner — Demonstration / Sandbox / Pilot only */}
            <div data-testid="verify-demo-notice">
              <EnvironmentBanner record={cert} />
            </div>
            {/* Hero */}
            <div className={`border-2 p-6 ${tone}`}>
              <div className="flex items-start gap-4 flex-wrap">
                <Icon className="h-10 w-10" />
                <div className="flex-1 min-w-[220px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="mono text-[10.5px] uppercase tracking-widest opacity-80">Certificate number</div>
                    <EnvironmentChip record={cert} testid="verify-env-chip" />
                  </div>
                  <div className="text-2xl font-semibold mt-0.5" data-testid="verify-cert-number">{cert.certificate_number}</div>
                  <div className="mono text-[10.5px] uppercase tracking-widest mt-2">status</div>
                  <div className="text-lg font-semibold mt-0.5">{state}</div>
                </div>
                <img alt="Badge" src={`${base}/api/public/certificates/${cert.certificate_number}/badge?variant=vertical`}
                     onError={(e) => { e.currentTarget.style.display = "none"; }}
                     className="h-24 w-auto bg-white/60 rounded-sm p-2" />
              </div>
            </div>

            {/* Grid */}
            <div className="grid md:grid-cols-2 gap-4">
              <Panel title="Asset" icon={ScrollText}>
                <F l="Type"        v={cert.asset_type} />
                <F l="Description" v={cert.asset_description} />
                <F l="Country"     v={cert.country} />
                <F l="Passport"    v={cert.digital_passport} link={cert.digital_passport && `/passport/${cert.digital_passport}`} />
                <F l="Verification ref" v={cert.verification_reference} />
              </Panel>
              <Panel title="Lifecycle" icon={Award}>
                <F l="Outcome"      v={cert.outcome} />
                <F l="Trust score"  v={cert.trust_score} />
                <F l="Issued"       v={fmt(cert.issued_at)} />
                <F l="Expires"      v={fmt(cert.expires_at)} />
                <F l="Version"      v={cert.version} />
                <F l="Hash (SHA-256)" v={data.integrity?.certificate_hash} mono />
              </Panel>
            </div>

            {/* Counter-signatures */}
            <Panel title="Counter-signatures" icon={Users}>
              {(data.counter_signatures || []).length === 0 && <div className="text-[13px] text-muted-foreground">No additional signatories on this certificate.</div>}
              {(data.counter_signatures || []).map((s, i) => (
                <div key={i} className="border-l-2 border-primary/40 pl-3 py-1" data-testid={`verify-cs-${i}`}>
                  <div className="text-[13px] font-medium">{s.signatory_name}{s.signatory_title ? ` · ${s.signatory_title}` : ""}</div>
                  <div className="mono text-[10.5px] uppercase tracking-widest text-muted-foreground">{s.role} · {s.organisation}</div>
                  <div className="text-[11.5px] text-muted-foreground">Signed {fmt(s.at)} · {s.note}</div>
                  <div className="mono text-[10px] text-muted-foreground truncate" title={s.signature}>sig: {s.signature?.slice(0, 32)}…</div>
                </div>
              ))}
            </Panel>

            {/* Country template */}
            {data.country_template && (
              <div className="border border-border bg-muted/30 p-4 text-[12.5px]" data-testid="verify-country-template">
                <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Legal notice · {data.country_template.name}</div>
                <div className="mt-1">{data.country_template.legal_footer}</div>
                <div className="mt-1 text-muted-foreground">{data.country_template.regulator}</div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-2 flex-wrap">
              <a href={`${base}/api/public/certificates/${cert.certificate_number}/pdf`} target="_blank" rel="noreferrer" data-testid="verify-pdf">
                <Button variant="outline"><Download className="h-3.5 w-3.5 mr-1" /> Download PDF</Button>
              </a>
              <a href={`${base}/api/public/certificates/${cert.certificate_number}/json`} target="_blank" rel="noreferrer" data-testid="verify-json">
                <Button variant="outline"><ExternalLink className="h-3.5 w-3.5 mr-1" /> Machine-readable JSON</Button>
              </a>
              <a href={`${base}/api/public/certificates/${cert.certificate_number}/badge?format=svg`} target="_blank" rel="noreferrer" data-testid="verify-badge">
                <Button variant="outline"><QrCode className="h-3.5 w-3.5 mr-1" /> Badge</Button>
              </a>
              <Link to="/verify/revocation-register"><Button variant="ghost">Public revocation register →</Button></Link>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}

const Panel = ({ title, icon: Icon, children }) => (
  <div className="border border-border bg-card">
    <div className="p-3 border-b border-border flex items-center gap-2"><Icon className="h-4 w-4 text-primary" /><div className="kicker">{title}</div></div>
    <div className="p-4 space-y-2">{children}</div>
  </div>
);

const F = ({ l, v, mono, link }) => (
  <div className="grid grid-cols-[130px_1fr] gap-3 text-[12.5px] items-start">
    <span className="mono text-[10px] uppercase tracking-widest text-muted-foreground pt-0.5">{l}</span>
    <span className={`min-w-0 break-words ${mono ? "mono text-[11px]" : ""}`}>
      {link ? <Link to={link} className="underline text-primary">{v || "—"}</Link> : (v ?? "—")}
    </span>
  </div>
);

const fmt = (v) => (v ? String(v).slice(0, 19).replace("T", " ") : "—");
